import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, LayersIcon, SendIcon, CheckCircle2Icon, MailIcon, GlobeIcon, ShieldCheckIcon, ExternalLinkIcon } from 'lucide-react';
type Step = 'search' | 'contacts' | 'sequence';
export function EcommerceDemo() {
  const [activeStep, setActiveStep] = useState<Step>('search');
  const [searchPhase, setSearchPhase] = useState<'loading' | 'done'>('loading');
  const [contactsPhase, setContactsPhase] = useState<'loading' | 'done'>('loading');
  const [sequencePhase, setSequencePhase] = useState<'loading' | 'done'>('loading');
  const searchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const contactsTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sequenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startSearch();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (contactsTimerRef.current) clearTimeout(contactsTimerRef.current);
      if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    };
  }, []);
  const startSearch = () => {
    setSearchPhase('loading');
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setSearchPhase('done'), 1200);
  };
  const startContacts = () => {
    setContactsPhase('loading');
    if (contactsTimerRef.current) clearTimeout(contactsTimerRef.current);
    contactsTimerRef.current = setTimeout(() => setContactsPhase('done'), 1400);
  };
  const startSequence = () => {
    setSequencePhase('loading');
    if (sequenceTimerRef.current) clearTimeout(sequenceTimerRef.current);
    sequenceTimerRef.current = setTimeout(() => setSequencePhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'search') startSearch();
    if (step === 'contacts') startContacts();
    if (step === 'sequence') startSequence();
  };
  return <section className="py-24 bg-white" data-id="element-879">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-880">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-881">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-882">
            Experience the{' '}
            <span className="text-blue-600" data-id="element-883">e-commerce database</span>
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-884">
            Search stores, view owner email lists, and launch sequences without
            ever leaving the platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-885">
          <button onClick={() => handleTabSwitch('search')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'search' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-886">
            <SearchIcon className="w-4 h-4" data-id="element-887" />
            Search e-commerce stores
          </button>
          <button onClick={() => handleTabSwitch('contacts')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'contacts' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-888">
            <LayersIcon className="w-4 h-4" data-id="element-889" />
            Enrich store data
          </button>
          <button onClick={() => handleTabSwitch('sequence')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'sequence' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-890">
            <SendIcon className="w-4 h-4" data-id="element-891" />
            Launch sequence
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-892">
          {activeStep === 'search' && <motion.div key="search" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-893">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-894">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-895">
                  Search e-commerce stores
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-896">
                  Query our e-commerce database by platform and category.
                </p>
                <div className="space-y-5" data-id="element-897">
                  <div data-id="element-898">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-899">
                      Platform
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-900">
                      Shopify
                    </div>
                  </div>
                  <div data-id="element-901">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-902">
                      Category
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-903">
                      Apparel
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-904">
                  {searchPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-905">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-906" />
                      Searching...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-907">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-908" />
                      18,477 stores found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-909">
                {searchPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-910">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-911" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-912">
                      Querying database...
                    </p>
                  </div>}
                {searchPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-913">
                    <div className="flex items-center justify-between mb-6" data-id="element-914">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-915">
                        Results
                      </h3>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-916">
                        18,477 found
                      </span>
                    </div>
                    <div className="space-y-3" data-id="element-917">
                      {[{
                  domain: 'kith.com',
                  platform: 'Shopify',
                  products: '1,200+',
                  rating: '4.8',
                  email: 'info@kith.com',
                  category: 'Apparel',
                  revenue: '$50M+'
                }, {
                  domain: 'allbirds.com',
                  platform: 'Shopify',
                  products: '350+',
                  rating: '4.6',
                  email: 'hello@allbirds.com',
                  category: 'Apparel',
                  revenue: '$100M+'
                }, {
                  domain: 'rothys.com',
                  platform: 'Shopify',
                  products: '400+',
                  rating: '4.5',
                  email: 'help@rothys.com',
                  category: 'Apparel',
                  revenue: '$50M+'
                }].map((biz, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm" data-id="element-918">
                          <div className="flex items-start justify-between mb-3" data-id="element-919">
                            <div data-id="element-920">
                              <h4 className="text-base font-bold text-slate-900" data-id="element-921">
                                {biz.domain}
                              </h4>
                              <div className="flex items-center gap-3 mt-1" data-id="element-922">
                                <span className="text-xs text-slate-500" data-id="element-923">
                                  {biz.platform}
                                </span>
                                <span className="text-xs text-slate-500" data-id="element-924">
                                  ★ {biz.rating}
                                </span>
                                <span className="text-xs text-slate-400" data-id="element-925">
                                  {biz.category}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-600" data-id="element-926">
                            <span className="flex items-center gap-1.5" data-id="element-927">
                              <MailIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-928" />
                              {biz.email}
                            </span>
                            <span className="text-slate-400" data-id="element-929">•</span>
                            <span data-id="element-930">{biz.products} products</span>
                            <span className="text-slate-400" data-id="element-931">•</span>
                            <span data-id="element-932">{biz.revenue} est. revenue</span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'contacts' && <motion.div key="contacts" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-933">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-934">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-935">
                  Enrich any e-commerce store
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-936">
                  Get 50+ data points on any store — tech stack, ratings,
                  revenue, owner contacts, and more.
                </p>
                <div className="space-y-5" data-id="element-937">
                  <div data-id="element-938">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-939">
                      Store domain
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 flex items-center gap-2" data-id="element-940">
                      <GlobeIcon className="w-4 h-4 text-slate-400" data-id="element-941" />
                      kith.com
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-942">
                  {contactsPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-943">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-944" />
                      Enriching store data...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-945">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-946" />
                      54 data points found
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-947">
                {contactsPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-948">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-949" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-950">
                      Enriching store profile...
                    </p>
                  </div>}
                {contactsPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-951">
                    <div className="flex items-center justify-between mb-5" data-id="element-952">
                      <div className="flex items-center gap-3" data-id="element-953">
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-954">
                          kith.com
                        </h3>
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2.5 py-1 rounded-full flex items-center gap-1" data-id="element-955">
                          <ShieldCheckIcon className="w-3 h-3" data-id="element-956" />
                          Verified
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full" data-id="element-957">
                        54 fields
                      </span>
                    </div>
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-id="element-958">
                      {[{
                  label: 'Platform',
                  value: 'Shopify Plus',
                  color: 'text-slate-900'
                }, {
                  label: 'Category',
                  value: 'Apparel > Streetwear & Footwear',
                  color: 'text-slate-900'
                }, {
                  label: 'Number of locations',
                  value: '3',
                  color: 'text-slate-900'
                }, {
                  label: 'Avg. ticket size',
                  value: '$185',
                  color: 'text-slate-900'
                }, {
                  label: 'Est. annual revenue',
                  value: '$50M+',
                  color: 'text-slate-900'
                }, {
                  label: 'Products listed',
                  value: '1,200+',
                  color: 'text-slate-900'
                }, {
                  label: 'Tech stack',
                  value: '',
                  color: '',
                  tags: ['Klaviyo', 'Google Analytics', 'Facebook Pixel', 'Affirm', 'Yotpo']
                }, {
                  label: 'Social profiles',
                  value: '',
                  color: '',
                  links: [{
                    name: 'Instagram',
                    handle: '@kith'
                  }, {
                    name: 'Twitter',
                    handle: '@KithSet'
                  }, {
                    name: 'TikTok',
                    handle: '@kith'
                  }]
                }, {
                  label: 'Owner contact',
                  value: '',
                  color: '',
                  contact: {
                    name: 'Ronnie Fieg',
                    role: 'Founder / CEO',
                    email: 'ronnie@kith.com'
                  }
                }].map((field, idx) => <motion.div key={idx} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: idx * 0.06,
                  duration: 0.3
                }} className={`flex items-start justify-between px-5 py-3.5 ${idx !== 8 ? 'border-b border-slate-100' : ''}`} data-id="element-959">
                          <span className="text-sm font-medium text-slate-500 w-40 shrink-0" data-id="element-960">
                            {field.label}
                          </span>
                          <div className="flex-1 text-right" data-id="element-961">
                            {field.tags ? <div className="flex flex-wrap justify-end gap-1.5" data-id="element-962">
                                {field.tags.map((tag, i) => <span key={i} className="text-xs font-medium text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg" data-id="element-963">
                                    {tag}
                                  </span>)}
                              </div> : field.links ? <div className="flex flex-wrap justify-end gap-2" data-id="element-964">
                                {field.links.map((link, i) => <span key={i} className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg flex items-center gap-1" data-id="element-965">
                                    {link.name}
                                    <ExternalLinkIcon className="w-3 h-3" data-id="element-966" />
                                  </span>)}
                              </div> : field.contact ? <div className="flex items-center justify-end gap-3" data-id="element-967">
                                <div className="text-right" data-id="element-968">
                                  <span className="text-sm font-semibold text-slate-900" data-id="element-969">
                                    {field.contact.name}
                                  </span>
                                  <span className="text-xs text-slate-400 ml-2" data-id="element-970">
                                    {field.contact.role}
                                  </span>
                                </div>
                                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg flex items-center gap-1" data-id="element-971">
                                  <MailIcon className="w-3 h-3" data-id="element-972" />
                                  {field.contact.email}
                                </span>
                              </div> : <span className={`text-sm font-semibold ${field.color}`} data-id="element-973">
                                {field.value}
                              </span>}
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'sequence' && <motion.div key="sequence" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-974">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-975">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-976">
                  Launch email sequence
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-977">
                  Send emails directly to e-commerce store contacts.
                </p>
                <div className="space-y-5" data-id="element-978">
                  <div data-id="element-979">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-980">
                      Campaign name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-981">
                      Shopify Apparel Outreach
                    </div>
                  </div>
                  <div data-id="element-982">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-983">
                      Sender account
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-984">
                      alex@yourcompany.com
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-985">
                  {sequencePhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-986">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-987" />
                      Launching sequence...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-988">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-989" />
                      Sequence active
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-990">
                {sequencePhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-991">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-992" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-993">
                      Starting email sequence...
                    </p>
                  </div>}
                {sequencePhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="min-h-[350px]" data-id="element-994">
                    {/* Sequence Builder Header */}
                    <div className="flex items-center justify-between mb-5" data-id="element-995">
                      <div className="flex items-center gap-3" data-id="element-996">
                        <h3 className="text-base font-bold text-slate-900" data-id="element-997">
                          Shopify Apparel Outreach
                        </h3>
                        <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full" data-id="element-998">
                          Draft
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-999">
                        <button className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg" data-id="element-1000">
                          Save draft
                        </button>
                        <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5" data-id="element-1001">
                          <SendIcon className="w-3 h-3" data-id="element-1002" />
                          Start sequence
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-1003">
                      {/* Left: Sequence Steps */}
                      <div className="w-48 shrink-0" data-id="element-1004">
                        <div className="text-[10px] font-bold text-slate-400 tracking-wider uppercase mb-3" data-id="element-1005">
                          Sequence steps
                        </div>
                        <div className="space-y-2" data-id="element-1006">
                          <div className="bg-white border-2 border-blue-200 rounded-xl p-3" data-id="element-1007">
                            <div className="text-xs font-bold text-blue-600" data-id="element-1008">
                              Initial outreach
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate" data-id="element-1009">
                              Quick question about {'{'} company_...{'}'}
                            </div>
                          </div>
                          <div className="flex justify-center" data-id="element-1010">
                            <div className="w-px h-3 bg-slate-200" data-id="element-1011"></div>
                          </div>
                          <div className="bg-white border border-slate-200 rounded-xl p-3" data-id="element-1012">
                            <div className="text-xs font-semibold text-slate-700" data-id="element-1013">
                              Follow up 1
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5 truncate" data-id="element-1014">
                              Re: Quick question about {'{'} compa...{'}'}
                            </div>
                            <div className="flex items-center gap-1 mt-1.5 text-[10px] text-slate-400" data-id="element-1015">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1016">
                                <circle cx="12" cy="12" r="10" strokeWidth="2" data-id="element-1017" />
                                <path strokeWidth="2" d="M12 6v6l4 2" data-id="element-1018" />
                              </svg>
                              Wait 5 days
                            </div>
                          </div>
                          <div className="flex justify-center" data-id="element-1019">
                            <div className="w-px h-3 bg-slate-200" data-id="element-1020"></div>
                          </div>
                          <div className="border border-dashed border-slate-300 rounded-xl p-2.5 text-center" data-id="element-1021">
                            <span className="text-[10px] font-medium text-slate-400" data-id="element-1022">
                              + Add step
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Email Editor */}
                      <div className="flex-1 bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-1023">
                        {/* From / To */}
                        <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-3 text-xs" data-id="element-1024">
                          <span className="text-slate-400 w-8" data-id="element-1025">From:</span>
                          <span className="text-blue-600 font-medium" data-id="element-1026">
                            + Connect your email
                          </span>
                        </div>
                        <div className="border-b border-slate-100 px-4 py-2.5 flex items-center gap-3 text-xs" data-id="element-1027">
                          <span className="text-slate-400 w-8" data-id="element-1028">To:</span>
                          <span className="text-slate-500" data-id="element-1029">
                            18,477 contacts from Shopify Apparel
                          </span>
                        </div>
                        {/* Subject */}
                        <div className="border-b border-slate-100 px-4 py-2.5" data-id="element-1030">
                          <span className="text-sm font-semibold text-slate-800" data-id="element-1031">
                            Quick question about {'{'} company_name{'}'}
                          </span>
                        </div>
                        {/* Toolbar */}
                        <div className="border-b border-slate-100 px-4 py-2 flex items-center gap-3 text-slate-400" data-id="element-1032">
                          <span className="font-bold text-xs" data-id="element-1033">B</span>
                          <span className="italic text-xs" data-id="element-1034">I</span>
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1035">
                            <path strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" data-id="element-1036" />
                          </svg>
                          <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-1037"></div>
                          <span className="text-[10px] font-medium text-slate-500" data-id="element-1038">
                            {'{ }'} Insert variable
                          </span>
                        </div>
                        {/* Body */}
                        <div className="px-4 py-4 text-xs text-slate-600 leading-relaxed space-y-3" data-id="element-1039">
                          <p data-id="element-1040">
                            Hi {'{'} first_name{'}'},
                          </p>
                          <p data-id="element-1041">
                            I came across {'{'} company_name{'}'} and was
                            impressed by your growth on Shopify.
                          </p>
                          <p data-id="element-1042">
                            I would love to connect and explore how we might be
                            able to help you streamline your operations and
                            drive more revenue.
                          </p>
                          <p data-id="element-1043">
                            Would you be open to a quick 15-minute call this
                            week?
                          </p>
                          <p data-id="element-1044">
                            Best,
                            <br data-id="element-1045" />
                            {'{'} sender_name{'}'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}